/**
 * Post-Purchase Page
 *
 * Full-page interstitial for upsell/downsell offers shown after checkout.
 * Unlike other pages, there is NO native storefront content underneath —
 * the template page_content IS the entire page (per spec section 5f).
 *
 * Flow: checkout → /post-purchase → accept → /thank-you
 *                                  → decline → downsell (if exists) → /thank-you
 */

'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useFunnelExperience,
  useFunnelContext,
  FunnelPageContent,
  FunnelModal,
  FunnelPopup,
  ActionExecutor,
  normalizeBlocks,
  addDeclinedStep,
} from '@template-project/storefront-funnel';
import type { FunnelOffer, StepPageContent, StepPageBlock, StepAction, DisplaySettings } from '@template-project/storefront-funnel';
import { useOrderDetails } from '@/lib/hooks/use-order-details';
import { useIntl } from '@/lib/intl';
import { useFunnelAddToCart } from '@/lib/hooks/use-funnel-add-to-cart';
import { storefrontLayoutLogger } from '@/lib/logger';

type TemplateLoadState = 'idle' | 'loading' | 'ready' | 'failed';

const FullPageContainer = styled.main`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background.primary};
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FallbackContainer = styled.div`
  max-width: 600px;
  margin: 80px auto;
  padding: 40px 24px;
  text-align: center;
`;

const FallbackTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FallbackDescription = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 24px;
`;

const AcceptButton = styled.button`
  display: block;
  width: 100%;
  max-width: 400px;
  margin: 0 auto 12px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.brand?.primary ?? '#22c55e'};
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const DeclineButton = styled.button`
  display: block;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: 12px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  border: none;
  cursor: pointer;
  text-decoration: underline;
`;

const ErrorMessage = styled.p`
  margin: 12px auto 0;
  max-width: 400px;
  color: #b91c1c;
  font-size: 14px;
  text-align: center;
`;

export function PostPurchasePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { acceptOffer, declineOffer, fetchTemplate } = useFunnelContext();
  const handleFunnelAddToCart = useFunnelAddToCart();
  const [accepting, setAccepting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const { countryConfig } = useIntl();

  const {
    orderId,
    orderProductIds,
    orderTotal,
    loading: orderLoading,
  } = useOrderDetails();
  const urlParameters = Object.fromEntries(searchParams.entries());

  const { loading, experience, offer, experienceType, overrideExperience } =
    useFunnelExperience('post_purchase', {
      url: '/post-purchase',
      country: countryConfig.countryCode,
      urlParameters,
      orderId: orderId ?? undefined,
      orderProductIds,
      orderTotal: orderTotal > 0 ? orderTotal : undefined,
    });

  const redirectedRef = useRef(false);
  const navigatedRef = useRef(false);
  const noOfferRef = useRef(false);
  const thankYouUrl = orderId ? `/thank-you?order_id=${encodeURIComponent(orderId)}` : '/thank-you';

  useEffect(() => {
    setSelectedOfferId(experience?.offers?.[0]?.id ?? experience?.offer?.id ?? null);
  }, [experience?.id, experience?.offers, experience?.offer?.id]);

  const navigateToThankYou = useCallback((replace = false) => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    navigate(thankYouUrl, replace ? { replace: true } : undefined);
  }, [navigate, thankYouUrl]);

  // If no experience found and neither order fetch nor funnel eval is loading,
  // redirect to thank-you (no upsell configured for this funnel).
  useEffect(() => {
    if (!orderLoading && !loading && !experience && !redirectedRef.current) {
      redirectedRef.current = true;
      navigateToThankYou(true);
    }
  }, [orderLoading, loading, experience, navigateToThankYou]);

  const activeOffer: FunnelOffer | null =
    (selectedOfferId ? experience?.offers?.find((item) => item.id === selectedOfferId) ?? null : null) ??
    experience?.offers?.[0] ??
    offer ??
    null;

  const safeOffer: FunnelOffer = activeOffer ?? {
    id: '',
    name: '',
    productId: '',
    variantId: '',
    headline: '',
    description: '',
    imageUrl: '',
    ctaText: 'Yes, Add to Order!',
    declineText: 'No thanks',
    pricingType: 'fixed',
    pricingValue: null,
    originalPrice: null,
  };

  // Fetch template content when templateId is set.
  const [templateContent, setTemplateContent] = useState<StepPageBlock[] | null>(null);
  const [templateDisplaySettings, setTemplateDisplaySettings] = useState<DisplaySettings | null>(null);
  const [templateLoadState, setTemplateLoadState] = useState<TemplateLoadState>('idle');
  useEffect(() => {
    if (!experience?.templateId) {
      setTemplateContent(null);
      setTemplateDisplaySettings(null);
      setTemplateLoadState('idle');
      return;
    }
    setTemplateContent(null);
    setTemplateDisplaySettings(null);
    setTemplateLoadState('loading');
    let cancelled = false;
    fetchTemplate(experience.templateId)
      .then((data) => {
        if (!cancelled) {
          if (data?.pageContent) {
            const blocks = Array.isArray(data.pageContent)
              ? data.pageContent
              : (data.pageContent as StepPageContent).blocks ?? null;
            setTemplateContent(blocks);
            setTemplateLoadState('ready');
          } else {
            setTemplateLoadState('failed');
          }
          if (data?.displaySettings) {
            setTemplateDisplaySettings(data.displaySettings ?? null);
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setTemplateLoadState('failed');
        storefrontLayoutLogger.warn('[PostPurchasePage] template fetch failed', {
          templateId: experience.templateId ?? null,
          error: err instanceof Error ? err.message : String(err),
        });
      });
    return () => { cancelled = true; };
  }, [experience?.templateId, fetchTemplate]);

  // OAF-009: Template priority must match FunnelExperience — templateContent wins when templateId is set
  const effectivePageContent: StepPageContent | StepPageBlock[] | null =
    experience?.templateId
      ? (templateContent ?? experience?.pageContent ?? null)
      : (experience?.pageContent ?? templateContent);
  // OAF-028: Compute effective displaySettings (step > template > undefined)
  const displaySettings = experience?.displaySettings ?? templateDisplaySettings ?? undefined;
  const blocks = normalizeBlocks(effectivePageContent);
  const hasBlocks = blocks.length > 0;
  const stepActions = experience?.actions;

  if (experience?.templateId && templateLoadState === 'loading') {
    return <LoadingContainer>Loading page...</LoadingContainer>;
  }

  const handleAccept = useCallback(async () => {
    if (!experience) return;
    setAccepting(true);
    setActionError(null);
    try {
      if (activeOffer) {
        // OAF-004: Post-purchase uses add_to_order (modifies existing order via checkout-middleware)
        // OAF-005: handleFunnelAddToCart removed — checkout-middleware handles order mutation
        const nav = await acceptOffer({
          stepId: experience.id,
          productId: activeOffer.productId,
          variantId: activeOffer.variantId,
          quantity: 1,
          action: 'add_to_order',
          orderId: orderId ?? undefined,
        });
        if (nav.nextStep) {
          navigatedRef.current = false;
          noOfferRef.current = false;
          overrideExperience(nav.nextStep);
          return;
        }
      }
      navigateToThankYou();
    } catch (err) {
      storefrontLayoutLogger.error('[PostPurchasePage] accept failed', err instanceof Error ? err : undefined, {
        stepId: experience.id,
        orderId: orderId ?? null,
      });
      setActionError('We could not apply this offer right now. Please try again.');
    } finally {
      setAccepting(false);
    }
  }, [experience, activeOffer, acceptOffer, orderId, overrideExperience, navigateToThankYou]);

  const handleDecline = useCallback(async () => {
    setActionError(null);
    if (!experience) {
      navigateToThankYou();
      return;
    }
    try {
      // OAF-016: Record declined step to prevent re-show on back navigation.
      addDeclinedStep(experience.id);
      const nav = await declineOffer(experience.id, orderId ?? undefined);
      if (nav.nextStep) {
        // Downsell available — swap in the downsell step without navigating away
        // OAF-014: Reset navigatedRef so accept/decline buttons work on downsell step
        navigatedRef.current = false;
        noOfferRef.current = false;
        overrideExperience(nav.nextStep);
        return;
      }
    } catch (err) {
      storefrontLayoutLogger.error('[PostPurchasePage] decline failed', err instanceof Error ? err : undefined, {
        stepId: experience.id,
        orderId: orderId ?? null,
      });
      setActionError('We could not process this action right now. Please try again.');
      return;
    }
    // No downsell or error — go to thank you
    navigateToThankYou();
  }, [experience, declineOffer, overrideExperience, orderId, navigateToThankYou]);

  const handleDismiss = useCallback(() => {
    navigateToThankYou();
  }, [navigateToThankYou]);

  // OAF-015: Guard via useEffect (React 18 safe — no navigate() in render body)
  useEffect(() => {
    if (experience && !activeOffer && !noOfferRef.current) {
      noOfferRef.current = true;
      navigateToThankYou(true);
    }
  }, [experience, activeOffer, navigateToThankYou]);

  // Wait for both the order fetch and the funnel evaluation before rendering.
  // orderLoading gates evaluate-step so the hook fires with accurate order context.
  if (orderLoading || loading) {
    return <LoadingContainer>Loading your special offer...</LoadingContainer>;
  }

  if (!experience) {
    return <LoadingContainer>Redirecting...</LoadingContainer>;
  }

  if (!activeOffer) {
    return <LoadingContainer>Redirecting...</LoadingContainer>;
  }

  // Log rendering case (dev only)
  if (import.meta.env.DEV) {
    const renderCase = hasBlocks ? 'B' : (stepActions?.length ?? 0) > 0 ? 'D' : 'E';
    storefrontLayoutLogger.info('[PostPurchasePage] render case', {
      stepId: experience.id,
      renderCase,
      blockCount: blocks.length,
      actionCount: stepActions?.length ?? 0,
      experienceType: experienceType ?? null,
    });
  }

  // EXP-005: Switch on experienceType to support modal/popup for post-purchase steps.
  // Default is 'page' (full-page interstitial, original behavior).
  const renderExperience = () => {
    switch (experienceType) {
      case 'modal':
        return (
          <FunnelModal
            offer={safeOffer}
            offers={experience.offers}
            selectedOfferId={selectedOfferId ?? undefined}
            onSelectOffer={setSelectedOfferId}
            pageContent={effectivePageContent}
            displaySettings={displaySettings}
            onAccept={handleAccept}
            onDecline={handleDecline}
            onDismiss={handleDismiss}
            accepting={accepting}
            errorMessage={actionError}
          />
        );
      case 'popup':
        return (
          <FunnelPopup
            offer={safeOffer}
            offers={experience.offers}
            selectedOfferId={selectedOfferId ?? undefined}
            onSelectOffer={setSelectedOfferId}
            pageContent={effectivePageContent}
            displaySettings={displaySettings}
            onAccept={handleAccept}
            onDecline={handleDecline}
            onDismiss={handleDismiss}
            accepting={accepting}
            errorMessage={actionError}
          />
        );
      default:
        // 'page' or unset — full-page interstitial (original behavior)
        return hasBlocks ? (
          <FunnelPageContent
            offer={safeOffer}
            offers={experience.offers}
            selectedOfferId={selectedOfferId ?? undefined}
            onSelectOffer={setSelectedOfferId}
            pageContent={effectivePageContent}
            displaySettings={displaySettings}
            onAccept={handleAccept}
            onDecline={handleDecline}
            onDismiss={handleDismiss}
            accepting={accepting}
            fullPage
            errorMessage={actionError}
          />
        ) : (
          <FallbackContainer>
            <FallbackTitle>{safeOffer.headline || 'Special Offer Just For You'}</FallbackTitle>
            <FallbackDescription>
              {safeOffer.description || 'We have a special deal for you before you go!'}
            </FallbackDescription>
            <AcceptButton onClick={handleAccept} disabled={accepting}>
              {accepting ? 'Adding to Order...' : safeOffer.ctaText}
            </AcceptButton>
            <DeclineButton onClick={handleDecline}>
              {safeOffer.declineText}
            </DeclineButton>
            {actionError && <ErrorMessage role="alert">{actionError}</ErrorMessage>}
          </FallbackContainer>
        );
    }
  };

  return (
    <FullPageContainer>
      {renderExperience()}

      {stepActions && stepActions.length > 0 && (
        <ActionExecutor
          actions={stepActions}
          stepId={experience.id}
          triggerLifecycle={experience.triggerLifecycle ?? 'session'}
          onAddToCart={handleFunnelAddToCart}
          onOfferAccept={async (offerId) => {
            setActionError(null);
            try {
              const nav = await acceptOffer({
                stepId: experience.id,
                productId: activeOffer?.productId ?? offerId,
                variantId: activeOffer?.variantId,
                quantity: 1,
                action: 'add_to_order',
                orderId: orderId ?? undefined,
              });
              if (nav.nextStep) {
                navigatedRef.current = false;
                noOfferRef.current = false;
                overrideExperience(nav.nextStep);
                return;
              }
              navigateToThankYou();
            } catch (err) {
              storefrontLayoutLogger.error('[PostPurchasePage] display_offer accept failed', err instanceof Error ? err : undefined, {
                stepId: experience.id,
                orderId: orderId ?? null,
              });
              const message = 'We could not apply this offer right now. Please try again.';
              setActionError(message);
              throw err instanceof Error ? err : new Error(message);
            }
          }}
          onOfferDecline={async () => {
            setActionError(null);
            try {
              addDeclinedStep(experience.id);
              const nav = await declineOffer(experience.id, orderId ?? undefined);
              if (nav.nextStep) {
                navigatedRef.current = false;
                noOfferRef.current = false;
                overrideExperience(nav.nextStep);
                return;
              }
              navigateToThankYou();
            } catch (err) {
              storefrontLayoutLogger.error('[PostPurchasePage] display_offer decline failed', err instanceof Error ? err : undefined, {
                stepId: experience.id,
                orderId: orderId ?? null,
              });
              const message = 'We could not process this action right now. Please try again.';
              setActionError(message);
              throw err instanceof Error ? err : new Error(message);
            }
          }}
        />
      )}
    </FullPageContainer>
  );
}
